import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useStore } from "../context/StoreContext";
import ProductCard from "../components/ProductCard";
import { useAnalytics } from "../context/AnalyticsProvider";
import "./styles/Products.css";

const Products = ({ products, showFilters = true, listName = "Product Grid", fireListViewEvent = false }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(1000); // Or whatever your reasonable upper limit is
  
  const [sortBy, setSortBy] = useState("newest");
  const [selectedTags, setSelectedTags] = useState([]);
  const [showSoldOut, setShowSoldOut] = useState(false); // ✅ Toggle state
  const [showNoProductsMessage, setShowNoProductsMessage] = useState(false);

  const lastFiredListRef = useRef(null);


  const analytics = useAnalytics();


  useEffect(() => {
    const timeout = setTimeout(() => {
      if (products.length === 0) {
        setShowNoProductsMessage(true);
      }
    }, 3000); // ⏳ Wait 3 seconds before showing "No products found"

    return () => clearTimeout(timeout); // ✅ Cleanup timeout on unmount
  }, [products]);



  const { categories } = useStore();







  const allTags = [...new Set(products.flatMap(product => product.tags))];

  const handleTagToggle = (tag) => {
    setSelectedTags((prevSelected) =>
      prevSelected.includes(tag)
        ? prevSelected.filter((t) => t !== tag)
        : [...prevSelected, tag]
    );
  };

  let filteredProducts = products.filter((product) => {
    const matchesSearch = !searchQuery || product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPriceMin = !priceMin || product.price >= Number(priceMin);
    const matchesPriceMax = !priceMax || product.price <= Number(priceMax);
    const matchesTags =
      selectedTags.length === 0 || selectedTags.every(tag => product.tags.includes(tag));
    const matchesStock = showSoldOut || product.stock > 0; // ✅ Only show in-stock unless toggled

    return matchesSearch && matchesPriceMin && matchesPriceMax && matchesTags && matchesStock;
  });

  const sortProducts = (products, criteria) => {
    switch (criteria) {
      case "price-low-high":
        return [...products].sort((a, b) => a.price - b.price);
      case "price-high-low":
        return [...products].sort((a, b) => b.price - a.price);
      case "name-a-z":
        return [...products].sort((a, b) => a.name.localeCompare(b.name));
      case "name-z-a":
        return [...products].sort((a, b) => b.name.localeCompare(a.name));
      case "newest":
      default:
        return [...products].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
  };

  filteredProducts = sortProducts(filteredProducts, sortBy);


  
  useEffect(() => {
    if (!fireListViewEvent) return;
    if (!filteredProducts || filteredProducts.length === 0) return;

    // Prevent duplicate firing per list/page load
    if (lastFiredListRef.current === listName) return;

    analytics.track("view_item_list", {
      item_list_name: listName,
      items: filteredProducts.map((item, index) => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        category: item.category_name,
        index: index + 1
      }))
    });

    lastFiredListRef.current = listName;

  }, [fireListViewEvent, filteredProducts, listName]);

  const nameCounts = products.reduce((acc, p) => {
    acc[p.name] = (acc[p.name] || 0) + 1;
    return acc;
  }, {});


  const duplicateNames = new Set(
  Object.entries(nameCounts)
        .filter(([name, count]) => count > 1)
        .map(([name]) => name)
);



  return (
    <div className="products-layout">
      {showFilters && (
        <aside className="products-sidebar">
          <div className="sidebar-sticky-wrapper">
            <h3>Filter Products</h3>

            <div className="filter-group">
              <h4>Search</h4>
              <div className="search-wrapper">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search for products by name"
                />
              </div>
            </div>

            <div className="filter-group price">
              <h4>Price</h4>

                {/* ✅ Manual Entry if desired */}
                <div className="price-inputs">
                  <input
                    type="number"
                    placeholder="Min"
                    min={0}
                    value={priceMin}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setPriceMin(value);

                    }}
                  />
                  <span>—</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={priceMax}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setPriceMax(value);
                    }}
                  />
                </div>

              {/* ✅ Price Range Slider */}
              <div className="price-slider">
                {/* Lower handle */}
                <label htmlFor="price-min" className="sr-only">Minimum Price</label>
                <input
                  id="price-min"
                  type="range"
                  min="0"
                  max="1000"
                  value={priceMin}
                  onChange={(e) => {
                    const value = Math.min(Number(e.target.value), priceMax - 1);
                    setPriceMin(value);
                  }}
                />
                {/* Upper handle */}
                <label htmlFor="price-max" className="sr-only">Maximum Price</label>
                <input
                  id="price-max"
                  type="range"
                  min="0"
                  max="1000"
                  value={priceMax}
                  onChange={(e) => {
                    const value = Math.max(Number(e.target.value), priceMin + 1);
                    setPriceMax(value);
                  }}
                />
              </div>

              
            </div>


            <div className="filter-group">
              <h4>Availability</h4>
              <div
                className="toggle-label"
                tabIndex="0"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setShowSoldOut((prev) => !prev);
                  }
                  if (e.key === ' ') {
                    e.preventDefault(); // Prevent page scroll
                    setShowSoldOut((prev) => !prev);
                  }
                }}
              >
              <label className="switch" aria-label="Toggle to show or hide sold out products">
                <input
                  type="checkbox"
                  checked={showSoldOut}
                  onChange={(e) => setShowSoldOut(e.target.checked)}
                />
                <div className="slider">
                  <div className="circle">
                    <svg
                      className="cross"
                      xmlSpace="preserve"
                      viewBox="0 0 365.696 365.696"
                      height="6"
                      width="6"
                    >
                      <g>
                        <path
                          fill="currentColor"
                          d="M243.188 182.86 356.32 69.726c12.5-12.5 12.5-32.766 0-45.247L341.238 9.398c-12.504-12.503-32.77-12.503-45.25 0L182.86 122.528 69.727 9.374c-12.5-12.5-32.766-12.5-45.247 0L9.375 24.457c-12.5 12.504-12.5 32.77 0 45.25l113.152 113.152L9.398 295.99c-12.503 12.503-12.503 32.769 0 45.25L24.48 356.32c12.5 12.5 32.766 12.5 45.247 0l113.132-113.132L295.99 356.32c12.503 12.5 32.769 12.5 45.25 0l15.081-15.082c12.5-12.504 12.5-32.77 0-45.25z"
                        ></path>
                      </g>
                    </svg>
                    <svg
                      className="checkmark"
                      xmlSpace="preserve"
                      viewBox="0 0 24 24"
                      height="10"
                      width="10"
                    >
                      <g>
                        <path
                          fill="currentColor"
                          d="M9.707 19.121a.997.997 0 0 1-1.414 0l-5.646-5.647a1.5 1.5 0 0 1 0-2.121l.707-.707a1.5 1.5 0 0 1 2.121 0L9 14.171l9.525-9.525a1.5 1.5 0 0 1 2.121 0l.707.707a1.5 1.5 0 0 1 0 2.121z"
                        ></path>
                      </g>
                    </svg>
                  </div>
                </div>
              </label>
              <p className="availability-label">
                Show Sold Out Items
              </p>
              </div>
            </div>


            <div className="filter-group">
              <h4>Categories</h4>
              <ul className="category-links">
                <li>
                  <Link to="/shop-knives/all-knives" className="all-categories-link">
                    All Categories
                  </Link>
                </li>
                {categories.length > 0 ? (
                  categories.map((category) => (
                    <li key={category.id}>
                      <Link to={`/shop-knives/category/${category.name.toLowerCase().replace(/\s+/g, "-")}`}>
                        {category.name}
                      </Link>
                    </li>
                  ))
                ) : (
                  <li>Loading categories...</li>
                )}
              </ul>
            </div>
          </div>
        </aside>
      )}

      <div className="products-section">
        <div className="products-header">
          <p className="products-results-count" aria-live="polite">
            {filteredProducts.length} {filteredProducts.length === 1 ? "results" : "results"}
          </p>

          {allTags.length > 0 && (
            <div className="tag-filter-container">
              <p>Filter Tags</p>
              <div className="tag-list">
                {allTags.map((tag, index) => (
                  <button
                    key={index}
                    className={`tag-button ${selectedTags.includes(tag) ? "active" : ""}`}
                    onClick={() => handleTagToggle(tag)}
                  >
                    {!selectedTags.includes(tag) && <i className="fa-solid fa-filter" />}
                    {selectedTags.includes(tag) && <i className="fa-solid fa-xmark" />}
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="sort-by-wrapper">
            <label htmlFor="sort-dropdown">Sort by:</label>
            <select id="sort-dropdown" className="sort-dropdown" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="newest">Newest</option>
              <option value="price-low-high">Price: Low to High</option>
              <option value="price-high-low">Price: High to Low</option>
              <option value="name-a-z">A to Z</option>
              <option value="name-z-a">Z to A</option>
            </select>
          </div>
          
        </div>
        <hr />
        <div className="products-grid">
          {products.length === 0 ? (
            showNoProductsMessage ? (
              <p className="no-products" role="alert" aria-live="assertive">
                No products available. Please check back later.
              </p>
            ) : (
              <div className="loader-wrapper">
                <div className="spinner"></div>
              </div>
            )
          ) : filteredProducts.length > 0 ? (
            filteredProducts.map((product, index) => <ProductCard key={product.id} product={product} listName={listName} index_position={index + 1} product_type={"Knives"} showId={duplicateNames.has(product.name)}/>)
          ) : (
            <p>No products available. Please adjust your filters or check back later.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Products;
